import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import SheetsConnectChoices, {
  type SheetsConnectChoice,
} from "../components/SheetsConnectChoices";
import { useAuth } from "../lib/useAuth";
import { getApi } from "../lib/get-api";
import { DatasourceDtoTypeEnum } from "../lib/generated/api-client";
import {
  startMergedGoogleSignIn,
  startSheetsSignIn,
  type SheetsPickerConfig,
} from "../lib/sheets-oauth";
import {
  adoptOAuthHandoff,
  clearOAuthNonce,
  isPlausibleEmail,
} from "../lib/oauth-handoff";
import {
  SHEETS_LINK_EXAMPLE,
  canonicalSheetsUrl,
  parseSheetsLink,
  shortSheetLabel,
} from "../lib/sheets-link";
import { sheetsLinkFieldState } from "../lib/sheets-link-field";
import {
  initialSheetsEntryMode,
  type SheetsEntryMode,
} from "../lib/sheets-entry-mode";
import {
  createSampleSheet,
  isSampleSheetUnavailable,
  startSampleSheetAnalysis,
} from "../lib/sample-sheet";
import {
  HEADER_ROW_CHOICES,
  canAutoGenerate,
  canConnect,
  canSelectTab,
  columnBadges,
  hasTabChoice,
  isEmptySheet,
  isViewerOnly,
  mergeTabFromProbe,
  needsHeaderRow,
  pagedNote,
  reviewSummary,
  selectedTabTitles,
  selectedTabs,
  skippedTabs,
  tabProblems,
  toggleTab,
  truncatedTabsNote,
} from "../lib/sheets-review";
import {
  clearSheetsLink,
  loadSheetsLink,
  saveSheetsLink,
} from "../lib/sheets-link-store";
import {
  bindWizardSession,
  createWizardSession,
  isSessionId,
  patchWizardSession,
  readWizardSession,
} from "../lib/sheets-wizard-session";
import {
  PICKER_TAB_PARAM,
  pickerTabPath,
  wizardPath,
  wizardUrlPatch,
} from "../lib/sheets-wizard-url";
import {
  actionLabel,
  apiErrorCode,
  connectedAsLabel,
  isAutoRetry,
  requestAccessMessage,
  sheetsFailure,
  sheetsFailureFromError,
  workspaceAdminRequestMessage,
  type SheetsFailure,
  type SheetsFailureAction,
  type SheetsFailureCase,
} from "../lib/sheets-failure";
import {
  checkSheetsLink,
  connectionAccount,
  convertSheetsFile,
  pickerAccessToken,
  probeSheets,
  type SheetsWizard,
} from "../lib/sheets-wizard-api";
import {
  __resetSheetsDatasourceInflight,
  findOrCreateSheetsDatasource,
  SheetsAlreadyConnectedError,
  type SheetsCreateBody,
} from "../lib/sheets-datasource-idempotent";
import {
  analyseCurrentTab,
  analyseElapsedLabel,
  analysePhaseLabel,
} from "../lib/analyse-phase-label";
import { pathForExistingDatasource } from "../lib/after-auth";
import { refreshDatasources } from "../lib/refresh-datasources";
import { isUiGenerationReady } from "../lib/ui-generation-status";
import type { AnalyzeAttemptDto } from "../lib/generated/api-client";

/** Stop waiting for analyse after this many ms and offer retry. */
const ANALYSE_TIMEOUT_MS = 180_000;

/**
 * MOSA-408. Where "Use a template" goes for a sheet with no column names.
 * Google's own gallery: every template there already has a header row, which
 * is the one thing the empty-sheet step needs.
 */
const SHEETS_TEMPLATE_GALLERY_URL =
  "https://docs.google.com/spreadsheets/u/0/?ftv=1";

/** Survives the Google full-page redirect; keyed by spreadsheet id. */
const switchGoogleStorageKey = (spreadsheetId: string) =>
  `od:sheets-switch-google:${spreadsheetId}`;

function readSwitchGoogleOffered(spreadsheetId: string): boolean {
  if (typeof window === "undefined" || !spreadsheetId) return false;
  try {
    return window.sessionStorage.getItem(switchGoogleStorageKey(spreadsheetId)) === "1";
  } catch {
    return false;
  }
}

function writeSwitchGoogleOffered(spreadsheetId: string): void {
  if (typeof window === "undefined" || !spreadsheetId) return;
  try {
    window.sessionStorage.setItem(switchGoogleStorageKey(spreadsheetId), "1");
  } catch {
    // Ignore quota / private mode.
  }
}

/**
 * MOSA-407. The address the merged consent found a password account for. It has
 * to survive the trip to the sign-in page and back, so the next consent can
 * preselect that Google account. It is the user's own email, nothing more.
 */
const linkEmailStorageKey = (sessionId: string) =>
  `od:sheets-link-email:${sessionId}`;

function readPendingLinkEmail(sessionId: string | null): string | null {
  if (typeof window === "undefined" || !sessionId) return null;
  try {
    return window.sessionStorage.getItem(linkEmailStorageKey(sessionId));
  } catch {
    return null;
  }
}

function writePendingLinkEmail(sessionId: string | null, email: string): void {
  if (typeof window === "undefined" || !sessionId || !email) return;
  if (!isPlausibleEmail(email)) return;
  try {
    window.sessionStorage.setItem(
      linkEmailStorageKey(sessionId),
      email.trim(),
    );
  } catch {
    // Ignore quota / private mode. The hint is a convenience, not a step.
  }
}

/**
 * MOSA-364. `#/connect/sheets?session=<id>` — the wizard a pasted sheet link
 * lands on, from the marketing site or from inside the app.
 *
 * MOSA-467. The link, the step and the Google connection id live in a
 * server-side session, and its id is in the URL. That single id goes through
 * the sign-in `next=`, the OAuth `state` and the Picker tab, so a reload never
 * asks for consent twice. `?sheet=` is still accepted on entry: the wizard
 * makes a session from it and then replaces the URL with `?session=`.
 * `sessionStorage` is only a fallback for when the session API fails.
 *
 * Six steps, each with its own status. Every failure becomes a `SheetsFailure`
 * from `lib/sheets-failure.ts`: plain copy, the exact fix, and the buttons that
 * carry it out. No raw codes, no stack traces.
 *
 * The Google Picker step needs `GOOGLE_PICKER_API_KEY` and
 * `GOOGLE_CLOUD_PROJECT_NUMBER`. Until they exist, step 4 explains that once
 * and the wizard keeps the link; nothing else in the app changes.
 */

type StepStatus = "waiting" | "working" | "done" | "needs_action";

type WizardStep = {
  id: number;
  title: string;
  status: StepStatus;
  /** Optional polite live region for screen readers (e.g. signed-out step 2). */
  announce?: string;
  detail: string;
};

declare global {
  interface Window {
    gapi?: {
      load: (api: string, callback: () => void) => void;
    };
    google?: {
      picker?: {
        PickerBuilder: new () => GooglePickerBuilder;
        DocsView: new (viewId?: string) => GooglePickerDocsView;
        ViewId: { SPREADSHEETS: string; DOCS: string };
        DocsViewMode: { LIST: string; GRID: string };
        Action: { PICKED: string; CANCEL: string };
        Response: { ACTION: string; DOCUMENTS: string };
        Document: { ID: string; NAME: string; MIME_TYPE: string };
      };
    };
  }
}

type GooglePickerBuilder = {
  addView: (view: unknown) => GooglePickerBuilder;
  setOAuthToken: (token: string) => GooglePickerBuilder;
  setDeveloperKey: (key: string) => GooglePickerBuilder;
  setAppId?: (appId: string) => GooglePickerBuilder;
  setCallback: (callback: (data: GooglePickerResponse) => void) => GooglePickerBuilder;
  setTitle?: (title: string) => GooglePickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
};

type GooglePickerResponse = {
  [key: string]: unknown;
};

/** Chainable, as Google's own typings are: every setter returns the view. */
type GooglePickerDocsView = {
  setIncludeFolders: (value: boolean) => GooglePickerDocsView;
  setSelectFolderEnabled: (value: boolean) => GooglePickerDocsView;
  setFileIds: (ids: string) => GooglePickerDocsView;
  setMode: (mode: string) => GooglePickerDocsView;
  /** MOSA-408. Open the Picker inside the folder the user pasted. */
  setParent?: (folderId: string) => GooglePickerDocsView;
};

/** Load the Picker script once, and resolve when `google.picker` exists. */
let pickerLoader: Promise<void> | null = null;
/** Test hook: clear the cached loader so each spec starts clean. */
export function __resetPickerLoader(): void {
  pickerLoader = null;
}

/** Test hook: clear create-inflight state between specs. */
export function __resetSheetsConnectTestState(): void {
  __resetPickerLoader();
  __resetSheetsDatasourceInflight();
}
function loadPicker(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("no window"));
  }
  if (window.google?.picker) {
    return Promise.resolve();
  }
  if (pickerLoader) {
    return pickerLoader;
  }
  pickerLoader = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById("od-google-picker");
    const done = () => {
      if (window.google?.picker) {
        resolve();
      } else {
        reject(new Error("picker missing"));
      }
    };
    if (existing) {
      window.gapi?.load("picker", done);
      return;
    }
    const script = document.createElement("script");
    script.id = "od-google-picker";
    script.src = "https://apis.google.com/js/api.js";
    script.async = true;
    script.onload = () => window.gapi?.load("picker", done);
    script.onerror = () => {
      pickerLoader = null;
      reject(new Error("picker script failed"));
    };
    document.head.appendChild(script);
  });
  return pickerLoader;
}

/**
 * MOSA-468. How long the wizard waits for the Picker to draw before it says the
 * window was blocked. Google's Picker draws an iframe in about 100 ms; 1.5 s
 * covers a slow machine without making a working Picker look broken.
 */
const PICKER_BLOCKED_MS = 1500;
/**
 * Every pending blocked-Picker timer. Cleaned up on unmount, so a Picker tab
 * that closes early does not write state afterwards.
 */
const blockedTimers = new Set<number>();

/**
 * MOSA-468. "Connected as <email> · Switch account". Shown on the Connect
 * Google step, the Picker step and the no-access row, so a wrong Google account
 * is visible before the Picker and fixable in one click.
 */
function ConnectedAs({
  email,
  onSwitch,
}: {
  email: string | null;
  onSwitch: () => void;
}) {
  return (
    <span
      className="flex flex-wrap items-center gap-2 text-xs text-od-muted"
      data-testid="sheets-connected-as"
    >
      <span className="text-od-text">{connectedAsLabel(email)}</span>
      <span aria-hidden>·</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onSwitch}
        data-testid="sheets-switch-account"
      >
        Switch account
      </Button>
    </span>
  );
}

const STATUS_RING: Record<StepStatus, string> = {
  waiting: "border-od-border text-od-muted",
  working: "border-od-primary text-od-primary-text",
  done: "border-od-success text-od-success",
  needs_action: "border-od-warning text-od-warning",
};

const STATUS_WORD: Record<StepStatus, string> = {
  waiting: "Waiting",
  working: "Working",
  done: "Done",
  needs_action: "Action needed",
};

/**
 * MOSA-697 item 15. One status style for every step: the word sits in the same
 * place and takes the colour of the ring beside it. A step the user has not
 * reached shows no word on screen and keeps the whole row muted; the word stays
 * in the markup for a screen reader.
 */
const STATUS_TEXT: Record<StepStatus, string> = {
  waiting: "sr-only",
  working: "text-od-primary-text",
  done: "text-od-success",
  needs_action: "text-od-warning",
};

/**
 * MOSA-697 item 14. Which rail step owns a failure. The rail has three steps
 * now, so each failure has to land on one of them: the link, the Google
 * connection, or the build. A link failure needs no table — step 1 reads the
 * link check itself.
 */
const GOOGLE_STEP_FAILURES: ReadonlySet<SheetsFailureCase> = new Set<SheetsFailureCase>([
  "not_signed_in",
  "link_required",
  "consent_denied",
  "scope_missing",
  "admin_policy",
  "picker_blocked",
  "picker_not_granted",
  "wrong_file",
  "spreadsheet_mismatch",
  "no_access",
  "not_configured",
  "already_connected",
]);

export default function SheetsConnect() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user, guest } = useAuth();

  const signedIn = Boolean(user) && !guest;
  const onlyDashAccount = (user?.email as string | undefined) ?? null;

  // Step 1 — the link. `?sheet=` wins, then the stored link. With a session in
  // the URL the server is the source of truth, so the stores are not read.
  // MOSA-908. `?entry=paste|browse` picks a path from the data-sources choices.
  const linkParam = params.get("sheet") ?? "";
  const sessionParam = params.get("session") ?? "";
  const entryParam = params.get("entry");
  const initialLink = isSessionId(sessionParam)
    ? ""
    : linkParam || loadSheetsLink() || "";
  const [link, setLink] = useState<string>(initialLink);
  // MOSA-908. Paste editor only when a link is already known, or the user
  // picked "Paste a Sheet link". Choice and Drive browse stay closed.
  const [editingLink, setEditingLink] = useState(() => {
    if (initialLink) return false;
    return entryParam === "paste";
  });
  const [linkInput, setLinkInput] = useState(link);
  const [entryMode, setEntryMode] = useState<SheetsEntryMode>(() => {
    if (initialLink) return "paste";
    if (entryParam === "browse") return "browse";
    if (entryParam === "paste") return "paste";
    return initialSheetsEntryMode({ hasLink: false, hasPickedFile: false });
  });
  const [sampleAvailable, setSampleAvailable] = useState(true);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);
  // True after the user types in the link field. Session restore must not wipe
  // the first keystrokes while the session API is still settling.
  const linkInputDirty = useRef(false);

  // MOSA-467 — the server-side session. `sessionLoading` is true until the
  // first read settles: the wizard does not act on a guess before then, or it
  // would ask for consent that the session already holds.
  const [hasSession, setHasSession] = useState(isSessionId(sessionParam));
  const [sessionId, setSessionId] = useState<string | null>(
    isSessionId(sessionParam) ? sessionParam : null,
  );
  const [sessionLoading, setSessionLoading] = useState(true);
  // True when the session API did not answer. Only then does the wizard fall
  // back to `sessionStorage` for the link.
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const sessionRestored = useRef(false);
  const sessionCreating = useRef(false);
  // MOSA-407. One consent redirect is handled once, and only while this page
  // is still on screen. The token hand-off finishes after an await, so it
  // needs both facts to outlive the render that stripped the status.
  const callbackHandled = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Step 3 — Google.
  const [picker, setPicker] = useState<SheetsPickerConfig | null>(null);
  // True once the Picker config has been fetched and answered. A null `picker`
  // is both "not loaded" and "not configured", and the two differ.
  const [pickerChecked, setPickerChecked] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  // MOSA-468. The Google account the connection belongs to, for "Connected as
  // <email> · Switch account". It comes from the API after consent and on every
  // load that restores a connection from the session, and never from a guess.
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [pickerGranted, setPickerGranted] = useState<{
    fileId: string;
    fileName: string;
  } | null>(null);

  // Steps 4–5 — the read.
  const [probe, setProbe] = useState<SheetsWizard | null>(null);
  // MOSA-408 — the review screen, which runs before any datasource exists.
  // The user has agreed to a panel with writes turned off.
  const [readOnlyAccepted, setReadOnlyAccepted] = useState(false);
  // MOSA-668 — the user has finished picking tabs. Only a file with more than
  // one usable tab waits for this; a single-tab sheet still needs no button.
  const [connectPressed, setConnectPressed] = useState(false);
  /** The tab whose header row is being re-read, by gid. */
  const [headerRowBusy, setHeaderRowBusy] = useState<number | null>(null);
  const [converting, setConverting] = useState(false);
  const [reading, setReading] = useState(false);
  const [failure, setFailure] = useState<SheetsFailure | null>(null);
  const [retryIn, setRetryIn] = useState<number | null>(null);

  // Step 6 — the panel.
  const [creating, setCreating] = useState(false);
  const [panelFailed, setPanelFailed] = useState(false);
  const datasourceIdRef = useRef<string | null>(null);
  /**
   * The spreadsheet generation has already started for. MOSA-408 made the probe
   * object change whenever one tab is re-read from a lower header row, so
   * object identity would start a second generation on every such change.
   */
  const generationStarted = useRef<string | null>(null);
  const createPanelAbort = useRef<(() => void) | null>(null);
  /** Monotonic id so a superseded createPanel cannot clear the newer run. */
  const createPanelRunId = useRef(0);
  const createPanelMounted = useRef(true);
  const completionHeadingRef = useRef<HTMLHeadingElement | null>(null);
  /** After one Switch Google account from a 409, do not offer it again. */
  const [switchGoogleOffered, setSwitchGoogleOffered] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Where "Open the panel" goes: the first dashboard route after generation.
  const [openPath, setOpenPath] = useState<string>("/data-sources");
  const [continuing, setContinuing] = useState(false);
  const [accessChecking, setAccessChecking] = useState(false);
  const [analyseProgress, setAnalyseProgress] = useState<AnalyzeAttemptDto | null>(
    null,
  );
  const [analyseElapsed, setAnalyseElapsed] = useState(0);

  const parsed = useMemo(() => parseSheetsLink(link), [link]);
  const parsedOk = parsed.ok;
  // MOSA-697 item 19. What the link field says while the user types. The
  // server link check still runs after the user accepts the link.
  const linkField = useMemo(() => sheetsLinkFieldState(linkInput), [linkInput]);
  const spreadsheetId = parsedOk ? parsed.spreadsheetId : "";

  // Restore after Google full-page redirect so Switch Google is not re-offered.
  useEffect(() => {
    if (!spreadsheetId) return;
    if (readSwitchGoogleOffered(spreadsheetId)) {
      setSwitchGoogleOffered(true);
    }
  }, [spreadsheetId]);

  // MOSA-407. Restore the address the merged consent reported, so the consent
  // that links Google still opens on it after the trip to the sign-in page.
  useEffect(() => {
    const stored = readPendingLinkEmail(sessionId);
    if (stored) {
      setPendingLinkEmail(stored);
    }
  }, [sessionId]);

  const callbackStatus = params.get("google_sheets");
  const linkErrorParam = params.get("link_error");
  const connectionParam = params.get("connectionId");
  /**
   * Single-use ticket from the hardened callback. Prefer this over a JWT in
   * the URL; redeem with the tab nonce via adoptOAuthHandoff.
   */
  const ticketParam = params.get("ticket");
  /**
   * Legacy JWT-in-URL from older API builds. Only adopted when this tab still
   * holds the OAuth nonce it minted — never from a cold crafted link.
   * TODO(remove-after-rollout): drop once every callback issues tickets only.
   */
  const tokenParam = params.get("token");
  /** MOSA-407. The address that already has a password account. */
  const linkEmailRaw = params.get("email");
  const linkEmailParam = isPlausibleEmail(linkEmailRaw)
    ? linkEmailRaw!.trim()
    : null;
  const [connectionId, setConnectionId] = useState<string | null>(null);
  /**
   * MOSA-407. Set once the merged consent reports a password account on this
   * address. It outlives the trip to the sign-in page, so the consent that
   * links Google can open on the right account.
   */
  const [pendingLinkEmail, setPendingLinkEmail] = useState<string | null>(null);
  const [linkCheck, setLinkCheck] = useState<{
    link: string;
    token: string;
    ok: boolean;
    title?: string;
  } | null>(null);
  const [linkCheckAttempt, setLinkCheckAttempt] = useState(0);
  const linkChecked =
    !token || (linkCheck?.link === link && linkCheck.token === token);
  const linkReady = parsedOk && linkChecked && (!token || linkCheck?.ok === true);
  const sheetTitleFromCheck =
    linkCheck?.link === link && linkCheck.ok ? linkCheck.title?.trim() : "";
  const confirmAccessLabel = sheetTitleFromCheck
    ? `Confirm access to ${sheetTitleFromCheck}`
    : "Confirm access to this sheet";

  useEffect(() => {
    if (!parsedOk || !token) return;
    let cancelled = false;
    checkSheetsLink(token, link)
      .then((result) => {
        if (cancelled) return;
        setLinkCheck({
          link,
          token,
          ok: result.ok,
          title: typeof result.title === "string" ? result.title : undefined,
        });
        if (!result.ok) {
          setFailure(sheetsFailureFromError(result, { stage: "link", onlyDashAccount }));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        const networkError = error?.code === "ERR_NETWORK" && !error?.response;
        setLinkCheck({ link, token, ok: networkError });
        if (!networkError) {
          setFailure(sheetsFailureFromError(error, { stage: "link", onlyDashAccount }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [link, parsedOk, token, onlyDashAccount, linkCheckAttempt]);

  // The stores are the fallback only, for when the session API fails. A live
  // session holds the link itself, so the stale value goes.
  useEffect(() => {
    if (!hasSession) return;
    clearSheetsLink();
  }, [hasSession]);

  useEffect(() => {
    if (!link || sessionUnavailable) return;
    saveSheetsLink(link);
  }, [link, sessionUnavailable]);

  /**
   * MOSA-467. Make or load the session once per page load.
   *
   * - `?session=` present: read it. The link, the step and the Google
   *   connection come from it.
   * - No session, `?sheet=` present: make one, then replace the URL with it.
   * - No session and no link: make an empty one, so the whole run of this
   *   wizard is under one id.
   *
   * When the API does not answer, the wizard keeps going with `sessionStorage`
   * and no session. That is the documented fallback, not a second design.
   */
  useEffect(() => {
    if (sessionRestored.current) return;
    sessionRestored.current = true;
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setSessionLoading(false);
    };

    const load = async () => {
      // A session is the source of truth when the URL has one, even if an old
      // `?sheet=` is still in the address bar.
      if (sessionId) {
        try {
          const session = await readWizardSession(token, sessionId);
          if (cancelled) return;
          if (session.link && !linkInputDirty.current) {
            setLink(session.link);
            setLinkInput(session.link);
            setEditingLink(false);
          } else if (session.link && linkInputDirty.current) {
            // Keep what the user typed; still record the session link as a
            // fallback only when the field is empty.
            setLink((current) => current || session.link || "");
          }
          if (session.googleConnectionId) {
            setConnectionId(session.googleConnectionId);
            setGoogleConnected(true);
          }
          if (session.pickedFileId) {
            setPickerGranted({
              fileId: session.pickedFileId,
              fileName: "",
            });
            if (!session.link) {
              const fromPick = canonicalSheetsUrl(session.pickedFileId);
              if (!linkInputDirty.current) {
                setLink(fromPick);
                setLinkInput(fromPick);
                setEditingLink(false);
              }
              setEntryMode("browse");
            } else {
              setEntryMode("paste");
            }
          } else if (session.link) {
            setEntryMode("paste");
          }
          settle();
          return;
        } catch (error) {
          if (cancelled) return;
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if (status === 404) {
            // Expired or unknown: start again. Keep the link when the URL still
            // has it; an expired session holds no data, so nothing leaks.
            const known = linkParam || link || "";
            setSessionId(null);
            setConnectionId(null);
            setGoogleConnected(false);
            await startSession(known);
            return;
          }
          if (status) {
            // The API answered, but not this call. Keep the id; another call
            // may still work.
            settle();
            return;
          }
          setSessionUnavailable(true);
          settle();
          return;
        }
      }
      await startSession(linkParam || loadSheetsLink() || "");
    };

    const startSession = async (fromLink: string) => {
      if (sessionCreating.current) {
        settle();
        return;
      }
      sessionCreating.current = true;
      try {
        const created = await createWizardSession(token, fromLink || undefined);
        if (cancelled) return;
        setHasSession(true);
        setSessionId(created.id);
        if (created.link && !linkInputDirty.current) {
          setLink(created.link);
          setLinkInput(created.link);
          setEditingLink(false);
        }
      } catch {
        // The session API is down: the wizard still works from storage.
        if (!cancelled) setSessionUnavailable(true);
      } finally {
        sessionCreating.current = false;
        settle();
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // One run per location. `token` changes from null to a value after
    // sign-in, and the read then repeats on the return to this page anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The URL carries the session once there is one. `session` wins over `sheet`,
  // so a reload reads the server state and not a stale link.
  useEffect(() => {
    if (sessionLoading) return;
    const patch = wizardUrlPatch({ session: sessionId, link });
    const currentSession = params.get("session") ?? "";
    const currentSheet = params.get("sheet") ?? "";
    if (
      (patch.session ?? "") === currentSession &&
      (patch.sheet ?? "") === currentSheet
    ) {
      return;
    }
    const next = new URLSearchParams(params);
    if (patch.session) next.set("session", patch.session);
    else next.delete("session");
    if (patch.sheet) next.set("sheet", patch.sheet);
    else next.delete("sheet");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, link, sessionLoading]);

  // A bad pasted link fails step 1 immediately, with the edit action. A
  // `?link_error=` code from the landing page counts the same way, so the
  // message is identical whichever page noticed it.
  useEffect(() => {
    const landed = linkErrorParam
      ? sheetsFailureFromError(
          { code: linkErrorParam },
          { stage: "link", onlyDashAccount },
        )
      : null;
    if (landed) {
      setFailure(landed);
      return;
    }
    if (!link) return;
    const result = parseSheetsLink(link);
    if (result.ok === false) {
      setFailure(
        sheetsFailureFromError(
          { code: result.reason },
          { stage: "link", onlyDashAccount },
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, linkErrorParam]);

  /**
   * MOSA-467. After sign-up or sign-in the user comes back with the session in
   * `next=`. Bind it now, so the Google connection that follows belongs to this
   * user and to nobody else. A 404 means the session expired while the user
   * signed in; the link on screen still works.
   */
  const boundFor = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId || !signedIn) return;
    const key = `${sessionId}:${user?.id ?? ""}`;
    if (boundFor.current === key) return;
    boundFor.current = key;
    void bindWizardSession(token, sessionId).catch(() => undefined);
  }, [sessionId, signedIn, user?.id, token]);

  // Step 3 continues: the consent callback returns with a status.
  //
  // MOSA-407. The merged sign-in also hands back an OnlyDash session, because
  // that one consent was the sign-up too. Adopt it before anything else: the
  // wizard session is now bound to the new user, so a session write with the
  // guest token the page loaded with would answer 404.
  //
  // Prefer `?ticket=` (nonce-bound, single-use). A bare `?token=` JWT is only
  // adopted when this tab still holds the nonce it minted — never when the
  // user is already signed in (a crafted link must not switch accounts).
  useEffect(() => {
    if (!callbackStatus) return;
    // Stripping the status below changes `callbackStatus`, which re-runs this
    // effect. Handle one redirect once: an effect that cancelled itself on that
    // re-run would drop everything the token hand-off does after its await.
    if (callbackHandled.current) return;
    callbackHandled.current = true;

    if (callbackStatus === "link_required" && linkEmailParam) {
      writePendingLinkEmail(sessionId, linkEmailParam);
      setPendingLinkEmail(linkEmailParam);
    }

    const applyStatus = (
      signInFailed: boolean,
      adoptedToken: string | null,
    ) => {
      if (!mounted.current) return;
      // The token to write the session with. After a merged sign-in that is the
      // one from the handoff, not this render's closure: the session is now
      // bound to the new user, and the pre-consent token would answer 404.
      const liveToken = (!signInFailed && adoptedToken) || token;
      if (signInFailed) {
        setFailure(
          sheetsFailure("consent_denied", {
            stage: "consent",
            onlyDashAccount,
          }),
        );
        return;
      }
      if (callbackStatus === "link_required") {
        // MOSA-407. The email already has a password account. Nothing was
        // created and nothing was changed; one password sign-in links Google.
        setFailure(
          sheetsFailure("link_required", {
            stage: "account",
            googleAccount: linkEmailParam,
            onlyDashAccount,
          }),
        );
        return;
      }
      if (callbackStatus === "connected" && connectionParam) {
        setConnectionId(connectionParam);
        setGoogleConnected(true);
        setFailure(null);
        // MOSA-467: save it as soon as it arrives, so a later reload reads it
        // from the session and not from this query string.
        if (sessionId) {
          void patchWizardSession(liveToken, sessionId, {
            googleConnectionId: connectionParam,
            step: 3,
          }).catch(() => undefined);
        }
        return;
      }
      applyFailureStatus();
    };

    const applyFailureStatus = () => {
      if (callbackStatus === "cancelled") {
        setFailure(
          sheetsFailure("consent_denied", {
            stage: "consent",
            googleAccount: null,
            onlyDashAccount,
          }),
        );
      } else if (callbackStatus === "not_configured") {
        setFailure(
          sheetsFailure("not_configured", {
            stage: "consent",
            onlyDashAccount,
          }),
        );
      } else if (
        callbackStatus === "scope_missing" ||
        callbackStatus === "admin_policy_enforced" ||
        callbackStatus === "org_internal"
      ) {
        // MOSA-468. Both come from the consent callback with no connection id:
        // the grant did not include drive.file, or a Workspace policy blocked the
        // app. The API names the account in the redirect only when it succeeded.
        setFailure(
          sheetsFailure(
            callbackStatus === "scope_missing" ? "scope_missing" : "admin_policy",
            { stage: "consent", onlyDashAccount },
          ),
        );
      } else if (
        callbackStatus === "reconnect_required" ||
        callbackStatus === "failed" ||
        callbackStatus === "connected"
      ) {
        setFailure(
          sheetsFailure("consent_denied", {
            stage: "consent",
            onlyDashAccount,
          }),
        );
      }
    };

    const finishHandoff = async () => {
      const handoff = await adoptOAuthHandoff({
        ticket: ticketParam,
        token: tokenParam,
        alreadySignedIn: signedIn,
      });
      if (handoff.status === "adopted") {
        applyStatus(false, handoff.token);
        return;
      }
      if (handoff.status === "failed") {
        // A ticket/token that will not redeem is a sign-in that did not happen.
        applyStatus(true, null);
        return;
      }
      // skipped: already signed in, or no handoff params
      applyStatus(false, null);
    };

    if (ticketParam || tokenParam) {
      void finishHandoff();
    } else {
      // cancelled / link_required / error with no handoff params: still drop
      // the start nonce so a later crafted ?token= cannot ride it.
      clearOAuthNonce();
      applyStatus(false, null);
    }

    // Strip the status so a reload does not replay it, and drop the ticket,
    // the token and the email with it: none may linger in the address bar or
    // in history. `session` stays: it is the key to the state that outlives
    // this page load.
    const next = new URLSearchParams(params);
    next.delete("google_sheets");
    next.delete("connectionId");
    next.delete("ticket");
    next.delete("token");
    next.delete("email");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackStatus]);

  /**
   * MOSA-468. Ask the API which Google account answered. The "Connected as"
   * line shows a wrong account before the Picker, which is the moment the user
   * can still switch. A failed lookup keeps the line blank; it never blocks the
   * read.
   */
  useEffect(
    () => () => {
      blockedTimers.forEach((timer) => window.clearTimeout(timer));
      blockedTimers.clear();
    },
    [],
  );

  const accountAskedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!googleConnected || !connectionId || !token) return;
    // The key holds both facts. A run that started before the connection was
    // known must not mark this connection as asked.
    const key = `${googleConnected}:${connectionId}`;
    if (accountAskedFor.current === key) return;
    accountAskedFor.current = key;
    let cancelled = false;
    // A failed or absent lookup leaves the line blank and never blocks the
    // read, the same way the Picker config is optional.
    Promise.resolve()
      .then(() => connectionAccount(token, connectionId))
      .then((account) => {
        if (!cancelled) setGoogleEmail(account.email);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [googleConnected, connectionId, token]);

  const loadPickerConfiguration = async () => {
    // The start call is also what proves configuration. It returns the Picker
    // key only when Google sign-in is set up.
    // MOSA-467: the session id rides in `state`, so the callback returns to
    // this run of the wizard and not to a cold start.
    //
    // MOSA-407: a caller with no account uses the merged route, where the one
    // consent is also the sign-up. A signed-in caller keeps the connect route,
    // so an existing user's reconnect asks for exactly the scopes it did
    // before. The link path runs here too, once the password sign-in is done:
    // `loginHint` names the address the app already knows has the account.
    const result = signedIn
      ? await startSheetsSignIn(
          token,
          "/connect/sheets",
          sessionId,
          pendingLinkEmail,
        )
      : await startMergedGoogleSignIn(token, "/connect/sheets", sessionId);
    if (result.status === "ready") {
      setPicker(result.picker);
      return result;
    }
    if (result.status === "signup_required") {
      setFailure(
        sheetsFailure("not_signed_in", { stage: "account", onlyDashAccount }),
      );
    } else if (result.status === "not_configured") {
      setFailure(
        sheetsFailure("not_configured", { stage: "consent", onlyDashAccount }),
      );
    } else {
      setFailure(
        sheetsFailureFromError(new Error(result.message), {
          stage: "consent",
          onlyDashAccount,
        }),
      );
    }
    return result;
  };

  // Step 4/5: probe as soon as we have a connection id and a valid link
  // (or a Drive pick with no pasted link).
  const runProbe = useCallback(async (
    connectionId: string,
    linkOverride?: string,
  ) => {
    const probeLink = (linkOverride ?? link).trim();
    const pickedId = pickerGranted?.fileId;
    if (!probeLink && !pickedId) return;
    setReading(true);
    setFailure(null);
    setRetryIn(null);
    try {
      const result = await probeSheets(token, {
        link: probeLink || undefined,
        pickedFileId: probeLink ? undefined : pickedId,
        connectionId,
      });
      setProbe(result);
    } catch (error) {
      const mapped = sheetsFailureFromError(error, {
        stage: "read",
        onlyDashAccount,
        googleAccount: null,
      });
      setFailure(mapped);
      if (isAutoRetry(mapped) && mapped.retryAfterSeconds) {
        setRetryIn(mapped.retryAfterSeconds);
      }
    } finally {
      setReading(false);
    }
  }, [link, token, onlyDashAccount, pickerGranted?.fileId]);

  /**
   * MOSA-408. Re-read one tab from another row, for a sheet whose real column
   * names sit under a title banner. The probe applies the row to the whole
   * file, so only the chosen tab is taken from the answer and the rest of the
   * review screen stays as the user left it.
   */
  const reprobeHeaderRow = useCallback(
    async (gid: number, headerRow: number) => {
      if (!link || !connectionId) return;
      setHeaderRowBusy(gid);
      try {
        const fresh = await probeSheets(token, {
          link,
          connectionId,
          headerRow,
        });
        setProbe((current) =>
          current ? mergeTabFromProbe(current, fresh, gid) : fresh,
        );
      } catch (error) {
        setFailure(
          sheetsFailureFromError(error, {
            stage: "read",
            onlyDashAccount,
            googleAccount: googleEmail,
          }),
        );
      } finally {
        setHeaderRowBusy(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [link, connectionId, token, onlyDashAccount, googleEmail],
  );

  /**
   * MOSA-408. Copy an Excel or CSV upload into a Google Sheet, then carry on
   * with the copy. The uploaded file is not changed, which is what the
   * "That is an Excel file" row says before this runs.
   */
  const convertExcelFile = useCallback(
    async (fileId: string) => {
      if (!connectionId) return;
      setConverting(true);
      try {
        const result = await convertSheetsFile(token, {
          fileId,
          connectionId,
        });
        const next = result.canonicalUrl;
        setLink(next);
        setLinkInput(next);
        setFailure(null);
        setProbe(null);
        setReadOnlyAccepted(false);
        setConnectPressed(false);
        generationStarted.current = null;
        datasourceIdRef.current = null;
        saveSheetsLink(next);
        if (sessionId) {
          void patchWizardSession(token, sessionId, { link: next }).catch(
            () => undefined,
          );
        }
      } catch (error) {
        setFailure(
          sheetsFailureFromError(error, {
            stage: "read",
            onlyDashAccount,
            googleAccount: googleEmail,
          }),
        );
      } finally {
        setConverting(false);
      }
    },
    [connectionId, token, sessionId, onlyDashAccount, googleEmail],
  );

  // Countdown for quota/5xx, then retry once on its own.
  const retryRef = useRef<number | null>(null);
  useEffect(() => {
    if (retryIn == null) return;
    if (retryIn <= 0) {
      if (connectionId) {
        void runProbe(connectionId);
      }
      return;
    }
    retryRef.current = window.setTimeout(() => setRetryIn((n) => (n ?? 1) - 1), 1000);
    return () => {
      if (retryRef.current) window.clearTimeout(retryRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryIn]);

  const openPicker = useCallback(async (
    connectionId: string,
    // MOSA-408. A pasted Drive folder opens the Picker inside that folder
    // instead of confirming one preselected file. `config` is for the folder
    // path, where a link refusal arrives before the Picker config is fetched.
    // MOSA-908. `mode: "browse"` lists every spreadsheet (no setFileIds).
    options: {
      folderId?: string;
      config?: SheetsPickerConfig;
      mode?: "confirm" | "browse";
    } = {},
  ) => {
    const pickerConfig = options.config ?? picker;
    if (!pickerConfig) {
      return;
    }
    setFailure(null);
    try {
      await loadPicker();
    } catch {
      setFailure(sheetsFailure("not_configured", { stage: "picker", onlyDashAccount }));
      return;
    }
    // The Picker needs a Google access token, not our connection id. Ask the
    // API for a short-lived one for this user's own connection.
    let accessToken: string;
    try {
      const result = await pickerAccessToken(token, connectionId);
      accessToken = result.accessToken;
    } catch (error) {
      setFailure(
        sheetsFailureFromError(error, {
          stage: "picker",
          onlyDashAccount,
        }),
      );
      return;
    }
    const googlePicker = window.google?.picker;
    if (!googlePicker) {
      setFailure(sheetsFailure("not_configured", { stage: "picker", onlyDashAccount }));
      return;
    }
    const folderId = options.folderId;
    const browseMode = options.mode === "browse";
    const spreadsheetId =
      folderId || browseMode ? "" : parsedOk ? parsed.spreadsheetId : "";
    const sheetLabel =
      sheetTitleFromCheck ||
      probe?.title ||
      pickerGranted?.fileName ||
      "this sheet";
    const view = new googlePicker.DocsView(
      googlePicker.ViewId.SPREADSHEETS,
    )
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false);
    if (folderId && view.setParent) {
      // Show the sheets in the folder the user pasted. No file is preselected:
      // the folder link never said which one they meant.
      view.setParent(folderId);
    } else if (spreadsheetId) {
      // Pre-select the pasted file so the user confirms that one file only.
      view.setFileIds(spreadsheetId);
    }
    // Browse mode: no setFileIds and no link compare. The user picks any sheet.
    // drive.file does not grant thumbnail access; LIST is the supported mode.
    // "VIEW" is not a DocsViewMode value and breaks the preselected confirm UI.
    const listMode = googlePicker.DocsViewMode?.LIST;
    if (listMode) {
      view.setMode(listMode);
    }
    const builder = new googlePicker.PickerBuilder()
      .addView(view)
      .setOAuthToken(accessToken)
      .setDeveloperKey(pickerConfig.apiKey);
    if (pickerConfig.projectNumber && builder.setAppId) {
      builder.setAppId(pickerConfig.projectNumber);
    }
    if (builder.setTitle) {
      builder.setTitle(
        folderId
          ? "Choose a sheet in this folder"
          : browseMode
            ? "Choose a Google Sheet"
            : `Confirm access to ${sheetLabel}`,
      );
    }
    // MOSA-468. A popup blocker or a cookie rule stops the Picker before it
    // draws, and then no callback ever arrives. If nothing answers shortly
    // after `setVisible(true)` and no Picker frame is in the page, say so and
    // offer the new-tab path. A real Picker answers PICKED or CANCEL.
    let answered = false;
    const blockedTimer = window.setTimeout(() => {
      blockedTimers.delete(blockedTimer);
      if (answered) return;
      const frame = document.querySelector("iframe.picker-dialog, .picker-dialog-bg");
      if (frame) return;
      setFailure(sheetsFailure("picker_blocked", { stage: "picker", onlyDashAccount }));
    }, PICKER_BLOCKED_MS);
    blockedTimers.add(blockedTimer);
    const pickerInstance = builder
      .setCallback((data) => {
        answered = true;
        window.clearTimeout(blockedTimer);
        blockedTimers.delete(blockedTimer);
        const action = data[googlePicker.Response.ACTION] as string | undefined;
        const docs = data[googlePicker.Response.DOCUMENTS] as
          | Array<{ [key: string]: string }>
          | undefined;
        if (action === googlePicker.Action.CANCEL) {
          setFailure(
            sheetsFailure("picker_not_granted", {
              stage: "picker",
              onlyDashAccount,
            }),
          );
          return;
        }
        if (action !== googlePicker.Action.PICKED) {
          return;
        }
        const doc = docs?.[0];
        const fileId = doc?.[googlePicker.Document.ID];
        const fileName = doc?.[googlePicker.Document.NAME] ?? "";
        if (!fileId) {
          setFailure(
            sheetsFailure("picker_not_granted", {
              stage: "picker",
              onlyDashAccount,
            }),
          );
          return;
        }
        if (spreadsheetId && fileId !== spreadsheetId) {
          // EM scope: the picker returned a different file than the link.
          setPickerGranted({ fileId, fileName });
          setFailure(
            sheetsFailure("wrong_file", { stage: "picker", onlyDashAccount }),
          );
          return;
        }
        setPickerGranted({ fileId, fileName });
        setFailure(null);
        // MOSA-908 browse: write the pick and the canonical link, then probe.
        const nextLink = browseMode
          ? canonicalSheetsUrl(fileId)
          : link || canonicalSheetsUrl(fileId);
        if (browseMode || !link) {
          setLink(nextLink);
          setLinkInput(nextLink);
          setEditingLink(false);
        }
        // MOSA-467: save the pick on the session, so a reload in this tab or a
        // reload in the new Picker tab finds the file again.
        if (sessionId) {
          void patchWizardSession(token, sessionId, {
            pickedFileId: fileId,
            ...(browseMode || !link ? { link: nextLink } : {}),
          }).catch(() => undefined);
        }
        void runProbe(connectionId, nextLink);
      })
      .build();
    pickerInstance.setVisible(true);
  }, [
    picker,
    token,
    parsed,
    parsedOk,
    onlyDashAccount,
    runProbe,
    sessionId,
    probe?.title,
    pickerGranted?.fileName,
    sheetTitleFromCheck,
    link,
  ]);

  const handleContinue = async () => {
    const browseWithoutLink = entryMode === "browse" && !link;
    if ((!linkReady && !browseWithoutLink) || continuing) {
      return;
    }
    // MOSA-407. No account is needed to press this: one Google screen both
    // creates the OnlyDash account and grants access to the file. The previous
    // "sign up first" gate is what this task removed for the Google path.
    setContinuing(true);
    try {
      // Step 3: get the consent URL and the Picker config.
      const result = await loadPickerConfiguration();
      if (result.status === "ready") {
        window.location.assign(result.url);
        return;
      }
    } finally {
      setContinuing(false);
    }
  };

  /**
   * Try to read the sheet. If the API can already see it (prior Picker grant
   * or a broader Google grant), skip the Picker. Otherwise open a confirm-access
   * Picker with the pasted file preselected — never a free "choose a file" UI.
   */
  const ensureSheetAccess = useCallback(
    async (connectionId: string) => {
      if (!link) return;
      setAccessChecking(true);
      setReading(true);
      setFailure(null);
      setRetryIn(null);
      try {
        const result = await probeSheets(token, { link, connectionId });
        setProbe(result);
        setPickerGranted({
          fileId: result.spreadsheetId,
          fileName: result.title || "",
        });
        if (sessionId) {
          void patchWizardSession(token, sessionId, {
            pickedFileId: result.spreadsheetId,
            step: 5,
          }).catch(() => undefined);
        }
      } catch (error) {
        const mapped = sheetsFailureFromError(error, {
          stage: "read",
          onlyDashAccount,
          googleAccount: googleEmail,
        });
        const needsPickerGrant =
          mapped.case === "no_access" || mapped.case === "picker_not_granted";
        if (needsPickerGrant && picker) {
          // Confirm access to the pasted file. Do not treat this as a free pick.
          void openPicker(connectionId);
          return;
        }
        setFailure(mapped);
        if (isAutoRetry(mapped) && mapped.retryAfterSeconds) {
          setRetryIn(mapped.retryAfterSeconds);
        }
      } finally {
        setReading(false);
        setAccessChecking(false);
      }
    },
    [
      link,
      token,
      sessionId,
      onlyDashAccount,
      googleEmail,
      picker,
      openPicker,
    ],
  );

  // MOSA-468. The Picker tab (`?picker=new`) does one job: open the Picker as
  // soon as the session has a connection. It never runs the read, because the
  // first tab owns the probe and the panel.
  const pickerTabOpened = useRef(false);
  useEffect(() => {
    if (params.get(PICKER_TAB_PARAM) !== "new") return;
    if (!googleConnected || !connectionId || !pickerChecked) return;
    if (pickerTabOpened.current) return;
    pickerTabOpened.current = true;
    if (picker) {
      void openPicker(
        connectionId,
        entryMode === "browse" && !link ? { mode: "browse" } : {},
      );
    } else {
      // No Picker key on this server: read the sheet without it, as the first
      // tab does.
      void runProbe(connectionId);
    }
  }, [
    params,
    googleConnected,
    connectionId,
    pickerChecked,
    picker,
    openPicker,
    runProbe,
    entryMode,
    link,
  ]);

  // MOSA-908. Drive browse: after Google is connected, open a free Picker.
  // No link is preselected. A cancelled or blocked Picker keeps its failure row.
  const browseOpened = useRef(false);
  useEffect(() => {
    if (params.get(PICKER_TAB_PARAM) === "new") return;
    if (entryMode !== "browse") return;
    if (!googleConnected || !connectionId || !pickerChecked) return;
    if (link || pickerGranted || probe || failure || sessionLoading) return;
    if (!picker) return;
    if (browseOpened.current) return;
    browseOpened.current = true;
    void openPicker(connectionId, { mode: "browse" });
  }, [
    params,
    entryMode,
    googleConnected,
    connectionId,
    pickerChecked,
    link,
    pickerGranted,
    probe,
    failure,
    sessionLoading,
    picker,
    openPicker,
  ]);

  const accessStarted = useRef<string | null>(null);
  useEffect(() => {
    // The Picker tab opens the Picker itself; the first tab must not race it.
    if (params.get(PICKER_TAB_PARAM) === "new") return;
    // Browse with no link yet: the browse effect owns the Picker.
    if (entryMode === "browse" && !link) return;
    if (
      !googleConnected ||
      !connectionId ||
      !linkReady ||
      !pickerChecked ||
      failure ||
      probe ||
      sessionLoading
    ) {
      return;
    }
    const key = `${connectionId}:${link}:${pickerGranted?.fileId ?? ""}`;
    if (accessStarted.current === key) return;
    accessStarted.current = key;
    // Always try a read first. A restored pickedFileId or a prior grant means
    // the Picker can stay closed. Only a failed read opens Confirm access.
    void ensureSheetAccess(connectionId);
  }, [
    params,
    entryMode,
    googleConnected,
    connectionId,
    linkReady,
    link,
    pickerChecked,
    failure,
    probe,
    sessionLoading,
    pickerGranted?.fileId,
    ensureSheetAccess,
  ]);

  // After the consent callback the Picker config has not been fetched in this
  // page load: `handleContinue` did that before the redirect away. Fetch it
  // again so "Pick this sheet" is available when we return connected.
  useEffect(() => {
    if (!googleConnected) return;
    if (picker) {
      setPickerChecked(true);
      return;
    }
    if (failure || pickerChecked) return;
    let cancelled = false;
    Promise.resolve(startSheetsSignIn(token, "/connect/sheets", sessionId))
      .then((result) => {
        if (cancelled || !result) return;
        if (result.status === "ready") {
          setPicker(result.picker);
        }
        // On ready with no Picker key, or on not_configured, the read still
        // runs: the sheet is readable through the OAuth grant alone.
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setPickerChecked(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConnected, picker, failure, pickerChecked]);

  const alreadyConnectedFailure = useCallback(
    (existingId: string): SheetsFailure => {
      const base = sheetsFailure(
        "already_connected",
        { stage: "panel", onlyDashAccount },
        { existingDatasourceId: existingId },
      );
      if (switchGoogleOffered) {
        return { ...base, actions: ["open_existing"] };
      }
      return base;
    },
    [onlyDashAccount, switchGoogleOffered],
  );

  const createPanel = useCallback(async () => {
    if (!probe || !connectionId) return;
    // Editing the link mid-build starts another createPanel — cancel the old
    // one first so it cannot write createdId/openPath or clear this run's
    // abort handle.
    createPanelAbort.current?.();
    const runId = ++createPanelRunId.current;
    const cancelled = { current: false };
    createPanelAbort.current = () => {
      cancelled.current = true;
    };
    const stillThisRun = () =>
      !cancelled.current && createPanelRunId.current === runId;
    setCreating(true);
    setPanelFailed(false);
    setFailure(null);
    setAnalyseProgress(null);
    setAnalyseElapsed(0);
    const startedAt = Date.now();
    try {
      const api = getApi({ token });
      const sheetUrl = canonicalSheetsUrl(probe.spreadsheetId, probe.gid);
      // MOSA-668. Only the ticked tabs become collections. The API checks the
      // titles against the file and refuses one it does not have.
      const body = {
        name: probe.title || "Google Sheet",
        type: DatasourceDtoTypeEnum.GOOGLE_SHEETS,
        connectionString: sheetUrl,
        googleConnectionId: connectionId ?? undefined,
        sheetsTabs: selectedTabTitles(probe),
      } as SheetsCreateBody;
      // Idempotent on the client: reloads, double effects and the Picker tab
      // must not create a second source for the same spreadsheet.
      let id: string;
      try {
        id = await findOrCreateSheetsDatasource(api.datasources, {
          spreadsheetId: probe.spreadsheetId,
          body,
        });
      } catch (error) {
        if (error instanceof SheetsAlreadyConnectedError) {
          if (!stillThisRun()) return;
          // Do not retry the same POST; surface open / switch-account actions.
          setPanelFailed(false);
          setFailure(alreadyConnectedFailure(error.existingId));
          return;
        }
        throw error;
      }
      if (!stillThisRun()) return;
      datasourceIdRef.current = id;

      // Align the session link with the picked sheet, then attach the id.
      // A prior Picker pick of a different file leaves the session on the
      // pasted link; patching datasourceId alone returns
      // wizard_session_datasource_spreadsheet_mismatch.
      if (sessionId) {
        try {
          await patchWizardSession(token, sessionId, { link: sheetUrl });
          await patchWizardSession(token, sessionId, {
            datasourceId: id,
            step: 6,
          });
        } catch (error) {
          if (
            apiErrorCode(error) ===
            "wizard_session_datasource_spreadsheet_mismatch"
          ) {
            if (!stillThisRun()) return;
            setPanelFailed(false);
            setFailure(
              sheetsFailureFromError(error, {
                stage: "panel",
                onlyDashAccount,
              }),
            );
            return;
          }
          // Session attach is best-effort; continue to analyse.
        }
      }
      if (!stillThisRun()) return;

      // Same async analyse + poll path as DataSourcesTable / AnalyseProgressModal.
      const initial = await api.analysis.createAnalyzeAttempt(id, {
        async: true,
      });
      if (!stillThisRun()) return;
      setAnalyseProgress(initial.data);
      let attempt = initial.data;
      while (attempt.status === "RUNNING" || attempt.status === "PENDING") {
        if (!stillThisRun()) return;
        if (Date.now() - startedAt > ANALYSE_TIMEOUT_MS) {
          setPanelFailed(true);
          setFailure(sheetsFailure("analyse_timeout", { stage: "panel", onlyDashAccount }));
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        if (!stillThisRun()) return;
        const fresh = await api.analysis.getAnalyzeAttempt(id, attempt.id);
        attempt = fresh.data;
        if (!stillThisRun()) return;
        setAnalyseProgress(attempt);
        setAnalyseElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }
      if (!stillThisRun()) return;
      if (attempt.status === "FAILED") {
        throw new Error(
          (attempt as { errorMessage?: string }).errorMessage ||
            "Analysis failed",
        );
      }

      const generation = await api.ui.generateUi({
        analyzeAttemptId: attempt.id,
        prompt: "Generate one page per entity as { page, entities[] }[].",
        async: false,
      });
      if (!stillThisRun()) return;
      // HTTP 201 can still carry FAILED, or a stale RUNNING row that already
      // has panels. Fail only on FAILED / empty unfinished bodies.
      if (!isUiGenerationReady(generation.data)) {
        throw new Error(
          generation.data.status === "FAILED"
            ? "Page generation failed"
            : "Page generation did not finish",
        );
      }
      // MOSA-364 review item 5: open the real dashboard route, not
      // /datasources/:id, which the router does not have. Take the first
      // generated dashboard: the rail hides single-entity twins, but the
      // dashboard route still draws them, so opening it works either way.
      const generated = (generation.data.panels ?? []).flatMap(
        (panel) => panel.dashboards ?? [],
      );
      const firstDashboard = generated[0];
      const nextOpenPath = firstDashboard?.id
        ? `/datasources/${id}/dashboards/${firstDashboard.id}`
        : `/data-sources`;
      // Same gap as sample create: the list store is stale until we refresh.
      await refreshDatasources();
      if (!stillThisRun()) return;
      setOpenPath(nextOpenPath);
      setCreatedId(id);
      clearSheetsLink();
    } catch (error) {
      if (!stillThisRun()) return;
      if (error instanceof SheetsAlreadyConnectedError) {
        setPanelFailed(false);
        setFailure(alreadyConnectedFailure(error.existingId));
        return;
      }
      setPanelFailed(true);
      setFailure({
        ...sheetsFailureFromError(error, {
          stage: "panel",
          onlyDashAccount,
        }),
        title: "The panel could not be generated",
        explanation:
          "OnlyDash read your sheet, but creating its panel did not finish.",
        fix: "Retry to generate the panel. Your sheet data is still shown below.",
      });
    } finally {
      // Only the active run may clear busy state / the abort handle.
      if (createPanelRunId.current === runId) {
        setCreating(false);
        createPanelAbort.current = null;
      }
    }
  }, [
    probe,
    connectionId,
    token,
    onlyDashAccount,
    sessionId,
    alreadyConnectedFailure,
  ]);

  // Cancel in-flight create only on unmount — not when token/sessionId change.
  useEffect(() => {
    createPanelMounted.current = true;
    return () => {
      createPanelMounted.current = false;
      createPanelAbort.current?.();
    };
  }, []);

  // MOSA-371 keeps this buttonless: the panel builds as soon as the review has
  // nothing left to decide. MOSA-408 adds the two decisions that do stop it —
  // a viewer-only file, and a sheet with no column names anywhere.
  useEffect(() => {
    if (
      !probe ||
      !canAutoGenerate(probe, { readOnlyAccepted, connectPressed })
    ) {
      return;
    }
    if (generationStarted.current === probe.spreadsheetId) {
      return;
    }
    generationStarted.current = probe.spreadsheetId;
    void createPanel();
  }, [probe, createPanel, readOnlyAccepted, connectPressed]);

  /**
   * MOSA-668. Tick or untick a tab. Only the ticked ones become panels, so
   * this is the one review decision that changes what the create call asks
   * for.
   */
  const onToggleTab = useCallback((gid: number, next: boolean) => {
    setProbe((current) => (current ? toggleTab(current, gid, next) : current));
  }, []);

  useEffect(() => {
    if (!createdId) return;
    completionHeadingRef.current?.focus();
  }, [createdId]);

  const headerlessTabs = probe?.tabs.filter((tab) => !tab.hasHeader) ?? [];
  const connectableTabs = probe?.tabs.filter((tab) => tab.hasHeader) ?? [];

  // MOSA-668. The tick boxes and the Connect button belong to the one decision
  // the review screen still holds open: which tabs become panels. They go away
  // as soon as the build starts, or when a failure owns the next action.
  const pickingTabs =
    !!probe && hasTabChoice(probe) && !creating && !createdId && !failure;

  // ---------------------------------------------------------------- steps
  // MOSA-697 item 14. Three steps, not six. The account step is gone (the one
  // Google consent makes the account), "Confirm access" folds into Connect
  // Google, and the read folds into the build: the user acts once per step.
  const googleFailure = failure && GOOGLE_STEP_FAILURES.has(failure.case);

  const browsePath = entryMode === "browse";
  // MOSA-908. On the choice screen the rail stays quiet: no paste "action
  // needed" while the user still picks paste, Drive, or sample.
  const onChoiceScreen = entryMode === "choice" && !link;
  const googleStatus: StepStatus =
    onChoiceScreen || (!parsedOk && !(browsePath && !link))
      ? "waiting"
      : googleFailure
        ? "needs_action"
        : pickerGranted || probe
          ? "done"
          : accessChecking
            ? "working"
            : googleConnected
              ? picker
                ? "needs_action"
                : "done"
              : browsePath
                ? "needs_action"
                : "waiting";

  // A failure the Google step does not own, raised after the sheet was read:
  // the build step is where the fix belongs.
  const readFailed = Boolean(
    failure && !googleFailure && !accessChecking && googleConnected && !probe,
  );

  const steps: WizardStep[] = [
    {
      id: 1,
      title: onChoiceScreen
        ? "Connect a sheet"
        : browsePath
          ? "Choose a sheet"
          : "Google Sheet link",
      status: onChoiceScreen
        ? "waiting"
        : browsePath
          ? pickerGranted || (linkReady && link)
            ? "done"
            : googleConnected
              ? "needs_action"
              : "waiting"
          : parsedOk && !linkChecked
            ? "working"
            : linkReady && link
              ? "done"
              : editingLink || linkCheck?.ok === false
                ? "needs_action"
                : "waiting",
      // MOSA-697 item 18. The sheet name from the link check, or a shortened
      // link. The card below shows the full link once; a second copy wrapped
      // over three lines here and moved every step under it. The read uses the
      // name as its own heading, so the rail does not take it from the probe.
      detail: onChoiceScreen
        ? "Paste a link, pick from Drive, or try a sample."
        : browsePath
          ? pickerGranted
            ? shortSheetLabel({
                title: pickerGranted.fileName || sheetTitleFromCheck,
                spreadsheetId: pickerGranted.fileId,
              }) || "Sheet chosen from Drive."
            : googleConnected
              ? "Opening Google Drive so you can pick a sheet…"
              : "Sign in with Google. Then pick a sheet from Drive."
          : parsedOk && link
            ? shortSheetLabel({
                title: sheetTitleFromCheck,
                spreadsheetId: parsed.spreadsheetId,
              })
            : "Paste the link from the sheet you want to turn into a panel.",
    },
    {
      id: 2,
      title: "Connect Google",
      status: googleStatus,
      detail: pickerGranted
        ? `Access confirmed for ${pickerGranted.fileName || "the sheet"}.`
        : accessChecking
          ? "Checking whether OnlyDash can read this sheet…"
          : browsePath && googleConnected && !probe && picker
            ? "Choose a sheet in Google Drive."
            : picker && googleConnected && !probe
              ? "Confirm access to the pasted sheet so OnlyDash can read it."
              : googleConnected
                ? googleEmail
                  ? `Connected as ${googleEmail}.`
                  : "Google is connected."
                : signedIn
                  ? browsePath
                    ? "OnlyDash asks Google so you can pick a sheet."
                    : "OnlyDash asks Google to read this one sheet."
                  : // MOSA-407. The consent also makes the OnlyDash account, so
                    // there is no sign-up page to visit first.
                    "This also creates your OnlyDash account.",
      /**
       * The status word is muted for a step the user has not reached, so a
       * screen reader gets the account fact from here instead.
       */
      announce:
        !signedIn && failure?.case !== "link_required"
          ? "Continue with Google creates your OnlyDash account. Your link is saved either way."
          : undefined,
    },
    {
      id: 3,
      title: "Build the panel",
      status: createdId
        ? "done"
        : creating || (reading && !accessChecking)
          ? "working"
          : probe || panelFailed || readFailed
            ? "needs_action"
            : "waiting",
      detail: createdId
        ? "Panel created."
        : creating
          ? analysePhaseLabel(analyseProgress?.progress)
          : reading && !accessChecking
            ? "Reading tabs and column names…"
            : probe
              ? `${probe.tabs.length} tab${
                  probe.tabs.length === 1 ? "" : "s"
                } found.`
              : "OnlyDash reads row 1 of each tab, then builds the panel.",
    },
  ];

  // ------------------------------------------------------------- actions
  // A failure row keeps the handler from the render that showed it, and that
  // render can predate the link check. These two refs hold the current facts,
  // so Try again uses the state at the moment of the click.
  const linkReadyRef = useRef(linkReady);
  linkReadyRef.current = linkReady;

  const startSampleFromChoice = async () => {
    setSampleError(null);
    setSampleLoading(true);
    try {
      const created = await createSampleSheet(token);
      // Start analysis here, not on Data Sources ?highlight=. Guests skip.
      if (signedIn) {
        await startSampleSheetAnalysis(token, created);
      }
      // Refresh before navigate: DataSources does not fetch on mount, so a
      // stale store would hide the new Sample card and miss ?highlight=.
      await refreshDatasources();
      navigate(pathForExistingDatasource(created));
    } catch (error) {
      if (isSampleSheetUnavailable(error)) {
        setSampleAvailable(false);
        setSampleError("The sample sheet is not available right now.");
        return;
      }
      setSampleError("Could not open the sample sheet. Try again.");
    } finally {
      setSampleLoading(false);
    }
  };

  const onConnectChoice = (choice: SheetsConnectChoice) => {
    if (choice === "paste") {
      setEntryMode("paste");
      setEditingLink(true);
      setSampleError(null);
      return;
    }
    if (choice === "browse") {
      setEntryMode("browse");
      setEditingLink(false);
      setSampleError(null);
      browseOpened.current = false;
      return;
    }
    void startSampleFromChoice();
  };

  /**
   * MOSA-468. Start Google consent for this wizard session. A typed failure
   * shows its own row and starts no redirect. Used by Continue with Google,
   * Switch account and the Try again of the scope and admin rows.
   */
  const startConsent = async () => {
    const result = await loadPickerConfiguration();
    if (result.status === "ready") {
      window.location.assign(result.url);
    }
  };

  const onAction = (action: SheetsFailureAction) => {
    switch (action) {
      case "edit_link":
        setEditingLink(true);
        setLinkInput(link);
        setFailure(null);
        return;
      case "sign_in_to_link":
      case "sign_up":
      case "sign_in": {
        // The session id goes in `next=`, so the whole run comes back to this
        // session, bound to the user who signs in.
        const nextPath = sessionId
          ? wizardPath(sessionId)
          : `/connect/sheets?sheet=${encodeURIComponent(link)}`;
        navigate({
          pathname: "/auth",
          search: `?next=${encodeURIComponent(nextPath)}`,
        });
        return;
      }
      case "try_again":
      case "retry_later":
        setFailure(null);
        if (panelFailed) {
          void createPanel();
          return;
        }
        if (!linkReadyRef.current) {
          setLinkCheck(null);
          setLinkCheckAttempt((attempt) => attempt + 1);
          return;
        }
        if (action === "retry_later") {
          setRetryIn(failure?.retryAfterSeconds ?? 10);
          return;
        }
        if (googleConnected && connectionId) {
          void runProbe(connectionId);
        } else {
          // MOSA-468. A scope or admin row comes from the consent callback and
          // has no connection. Try again must ask for consent, which also works
          // from the failure card, where Continue with Google is not shown.
          void startConsent();
        }
        return;
      case "switch_account":
        setGoogleConnected(false);
        setProbe(null);
        setReadOnlyAccepted(false);
        setConnectPressed(false);
        setPickerGranted(null);
        setFailure(null);
        void loadPickerConfiguration().then((result) => {
          if (result.status === "ready") {
            window.location.assign(result.url);
          }
        });
        return;
      case "copy_request_message":
        if (typeof navigator !== "undefined" && navigator.clipboard) {
          void navigator.clipboard.writeText(
            requestAccessMessage({
              googleAccount: failure?.googleAccount ?? null,
              sheetUrl: canonicalSheetsUrl(
                parsedOk ? parsed.spreadsheetId : "",
              ),
              onlyDashAccount,
            }),
          );
        }
        return;
      case "open_request_access":
        if (parsedOk) {
          window.open(
            `https://docs.google.com/spreadsheets/d/${parsed.spreadsheetId}/edit`,
            "_blank",
            "noopener,noreferrer",
          );
        }
        return;
      case "open_picker":
        if (connectionId) {
          void openPicker(connectionId);
        }
        return;
      // ------------------------------------------------------- MOSA-408
      case "pick_in_folder": {
        // The folder id rides along on the drive_folder_link refusal, so the
        // Picker opens where the user already was. The refusal itself stops the
        // Picker config from being fetched, so fetch it here before opening.
        const folderId = failure?.folderId;
        if (!connectionId || !folderId) return;
        void (async () => {
          let config = picker;
          if (!config) {
            const result = await Promise.resolve(
              startSheetsSignIn(token, "/connect/sheets", sessionId),
            ).catch(() => null);
            if (result?.status === "ready" && result.picker) {
              config = result.picker;
              setPicker(result.picker);
            }
          }
          if (!config) {
            setFailure(
              sheetsFailure("not_configured", {
                stage: "picker",
                onlyDashAccount,
              }),
            );
            return;
          }
          await openPicker(connectionId, { folderId, config });
        })();
        return;
      }
      case "convert_to_sheet":
        if (failure?.driveFileId) {
          void convertExcelFile(failure.driveFileId);
        }
        return;
      case "connect_read_only":
        // The API decides readOnly from Drive, not from this click; the click
        // only says "build the panel anyway". The effect above then starts it.
        setReadOnlyAccepted(true);
        // MOSA-668. This is the connect button for a viewer-only file, so it
        // answers the tab question as well: the ticks are already made.
        setConnectPressed(true);
        setFailure(null);
        return;
      case "request_edit_access":
      case "add_starter_headers":
        // Both open the sheet itself: Drive shows its own Request access there,
        // and an empty sheet is where the column names have to be typed.
        if (parsedOk) {
          window.open(
            canonicalSheetsUrl(parsed.spreadsheetId),
            "_blank",
            "noopener,noreferrer",
          );
        }
        return;
      case "use_template":
        window.open(
          SHEETS_TEMPLATE_GALLERY_URL,
          "_blank",
          "noopener,noreferrer",
        );
        return;
      case "copy_admin_request":
        if (typeof navigator !== "undefined" && navigator.clipboard) {
          void navigator.clipboard.writeText(
            workspaceAdminRequestMessage({
              googleAccount: googleEmail,
              onlyDashAccount,
            }),
          );
        }
        return;
      case "open_picker_new_tab": {
        // MOSA-468. A new tab is not subject to the popup blocker that stopped
        // the Picker. The tab opens the wizard itself with `picker=new` and the
        // session id, so the pick lands in the same run of the wizard.
        const target = `/#${pickerTabPath(sessionId)}`;
        if (typeof window !== "undefined") {
          window.open(target, "_blank", "noopener,noreferrer");
        }
        return;
      }
      case "pick_pasted_sheet":
        setFailure(null);
        setPickerGranted(null);
        setProbe(null);
        setReadOnlyAccepted(false);
        setConnectPressed(false);
        generationStarted.current = null;
        datasourceIdRef.current = null;
        if (connectionId) {
          void openPicker(connectionId);
        }
        return;
      case "use_picked_file":
        if (pickerGranted) {
          const next = canonicalSheetsUrl(pickerGranted.fileId);
          setLink(next);
          setLinkInput(next);
          setFailure(null);
          setProbe(null);
          setReadOnlyAccepted(false);
          setConnectPressed(false);
          generationStarted.current = null;
          datasourceIdRef.current = null;
          // Update the session link first so a later datasourceId PATCH matches.
          if (sessionId) {
            void patchWizardSession(token, sessionId, {
              link: next,
              pickedFileId: pickerGranted.fileId,
            }).catch(() => undefined);
          }
          if (connectionId) {
            setReading(true);
            void probeSheets(token, { link: next, connectionId })
              .then((result) => {
                setProbe(result);
              })
              .catch((error) =>
                setFailure(
                  sheetsFailureFromError(error, {
                    stage: "read",
                    onlyDashAccount,
                  }),
                ),
              )
              .finally(() => setReading(false));
          }
        }
        return;
      case "open_existing": {
        const existingId = failure?.existingDatasourceId;
        if (!existingId) return;
        void (async () => {
          try {
            const api = getApi({ token });
            const response = await api.datasources.getDatasource(existingId);
            await refreshDatasources();
            navigate(pathForExistingDatasource(response.data as any));
          } catch {
            await refreshDatasources();
            navigate(
              `/data-sources?highlight=${encodeURIComponent(existingId)}`,
            );
          }
        })();
        return;
      }
      case "use_my_account":
        // Same path as switch_account: reconnect Google for this sheet.
        // After this offer, a later 409 no longer shows Switch Google account.
        setSwitchGoogleOffered(true);
        if (spreadsheetId) writeSwitchGoogleOffered(spreadsheetId);
        setGoogleConnected(false);
        setProbe(null);
        setPickerGranted(null);
        setReadOnlyAccepted(false);
        setConnectPressed(false);
        setFailure(null);
        generationStarted.current = null;
        datasourceIdRef.current = null;
        void loadPickerConfiguration().then((result) => {
          if (result.status === "ready") {
            window.location.assign(result.url);
          }
        });
        return;
      default:
        return;
    }
  };

  const progress = analyseProgress?.progress;
  const progressDone = progress?.done ?? 0;
  const progressTotal = progress?.total ?? 0;
  const progressLabel = analysePhaseLabel(progress);
  const progressCurrentTab = analyseCurrentTab(progress);
  const columnCount = connectableTabs.reduce(
    (sum, tab) => sum + tab.columns.length,
    0,
  );

  const wizardChrome = (
    <aside className="min-w-0 [overflow-wrap:anywhere] lg:pt-1">
      <a
        href="/#/data-sources"
        className="inline-flex min-h-11 min-w-11 items-center rounded-sm text-xs text-od-muted hover:text-od-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-bg sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
      >
        ← Back to data sources
      </a>
      {/*
        MOSA-697 item 17. One header for the screen. The "ONLYDASH" eyebrow is
        gone: the top bar already carries the wordmark. On the finished screen
        this title steps aside, because "Your panel is ready" is the heading
        there.
      */}
      {createdId ? (
        <p className="mt-4 text-2xl font-semibold tracking-tight text-od-text">
          Connect a Google Sheet
        </p>
      ) : (
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-od-text">
          Connect a Google Sheet
        </h1>
      )}
      <p className="mt-2 text-sm text-od-muted leading-relaxed">
        OnlyDash builds a panel from the tabs in your sheet.
      </p>
      <ol className="mt-8 space-y-3" data-testid="sheets-steps">
        {steps.map((step) => (
          <li key={step.id} className="flex gap-3">
            <span
              className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold ${STATUS_RING[step.status]}`}
              aria-hidden
            >
              {step.status === "working" ? (
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : step.status === "done" ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                step.id
              )}
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-baseline gap-2">
                <span
                  className={`text-sm font-semibold ${
                    step.status === "waiting" ? "text-od-muted" : "text-od-text"
                  }`}
                >
                  {step.title}
                </span>
                <span
                  className={`text-xs font-medium uppercase tracking-wide ${STATUS_TEXT[step.status]}`}
                  data-testid={`step-status-${step.id}`}
                >
                  {STATUS_WORD[step.status]}
                </span>
              </div>
              <p className="mt-0.5 break-words text-xs text-od-muted">
                {step.detail}
              </p>
              {step.announce ? (
                <p
                  className="sr-only"
                  role="status"
                  aria-live="polite"
                  data-testid={`step-announce-${step.id}`}
                >
                  {step.announce}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );

  // ---------------------------------------------------------------- views
  if (createdId) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-od-bg via-[color-mix(in_srgb,var(--od-primary)_6%,var(--od-bg))] to-[color-mix(in_srgb,var(--od-accent)_8%,var(--od-bg))] px-4 py-8 md:py-12"
        data-testid="sheets-connect-done"
      >
        <div className="mx-auto grid max-w-5xl items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          {wizardChrome}
          <div className="rounded-2xl border border-od-border bg-od-surface/95 p-6 shadow-sm md:p-8">
            <div className="flex items-start gap-4">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-od-primary to-od-accent text-od-primary-contrast shadow-md"
                aria-hidden
              >
                <CheckIcon sx={{ fontSize: 28 }} />
              </div>
              <div className="min-w-0">
                <h1
                  ref={completionHeadingRef}
                  tabIndex={-1}
                  className="text-2xl font-semibold tracking-tight text-od-text outline-none"
                >
                  Your panel is ready
                </h1>
                <p className="mt-2 text-base text-od-muted leading-relaxed">
                  Built your panel from {connectableTabs.length} tab
                  {connectableTabs.length === 1 ? "" : "s"} and {columnCount}{" "}
                  column{columnCount === 1 ? "" : "s"}
                  {probe?.title ? (
                    <>
                      {" "}
                      in{" "}
                      <span className="font-medium text-od-text">
                        {probe.title}
                      </span>
                    </>
                  ) : null}
                  .
                </p>
              </div>
            </div>

            <ul className="mt-6 space-y-2" data-testid="sheets-completion-tabs">
              {connectableTabs.map((tab) => (
                <li
                  key={tab.gid}
                  className="flex items-center justify-between gap-3 rounded-lg border border-od-border bg-od-surface-2/60 px-3 py-2.5"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-od-text">
                    <CheckIcon
                      sx={{ fontSize: 16 }}
                      className="text-od-success"
                      aria-hidden
                    />
                    {tab.title}
                  </span>
                  <span className="text-xs text-od-muted">
                    {tab.columns.length} column
                    {tab.columns.length === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>

            {headerlessTabs.length > 0 && (
              <p
                className="mt-4 text-sm text-od-muted"
                data-testid="sheets-headerless-tabs"
              >
                OnlyDash skips these tabs:{" "}
                {headerlessTabs.map((tab) => `“${tab.title}”`).join(", ")}. It
                uses the other tabs.
              </p>
            )}

            <div className="mt-8 flex flex-col gap-2 sm:flex-row">
              <Button
                variant="primary"
                size="md"
                onClick={() => navigate(openPath)}
              >
                Open the panel
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => navigate("/connect/sheets")}
              >
                Connect another sheet
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Restoring a known session needs a brief busy state so we do not flash the
  // wrong step. A cold paste (no session id) keeps the form visible.
  if (sessionLoading && hasSession) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-od-bg px-4"
        data-testid="sheets-connect-loading"
      >
        <div className="flex items-center gap-3 text-od-muted" role="status">
          <span
            className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-od-border border-t-od-primary"
            aria-hidden
          />
          <span className="text-sm">Opening the connect wizard…</span>
        </div>
      </div>
    );
  }

  // Choice screen: centre the rail + card as one calm mid-viewport block.
  // Other steps stay top-aligned so long review content scrolls naturally.
  return (
    <div
      className={
        onChoiceScreen
          ? "flex min-h-screen flex-col justify-center bg-gradient-to-br from-od-bg via-[color-mix(in_srgb,var(--od-primary)_5%,var(--od-bg))] to-[color-mix(in_srgb,var(--od-accent)_6%,var(--od-bg))] px-4 py-10 md:py-16"
          : "min-h-screen bg-gradient-to-br from-od-bg via-[color-mix(in_srgb,var(--od-primary)_5%,var(--od-bg))] to-[color-mix(in_srgb,var(--od-accent)_6%,var(--od-bg))] px-4 py-8 md:py-12"
      }
      data-testid="sheets-connect"
    >
      <div
        className={
          onChoiceScreen
            ? "mx-auto grid w-full max-w-5xl grid-cols-[minmax(0,1fr)] items-center gap-8 lg:grid-cols-[220px_minmax(0,1fr)]"
            : "mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)] items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)]"
        }
      >
        {wizardChrome}

        <div className="min-w-0 rounded-xl border border-od-border bg-od-surface p-5 shadow-sm [overflow-wrap:anywhere] md:p-7">
          <div className="space-y-5">
            {entryMode === "choice" && !link && (
              <div className="space-y-2">
                <SheetsConnectChoices
                  onChoose={onConnectChoice}
                  onMoreConnections={() => navigate("/data-sources?connect=postgres")}
                  sampleAvailable={sampleAvailable}
                  sampleLoading={sampleLoading}
                  title="Connect a Google Sheet"
                  detail="Paste a link. Choose a file from Drive. Or try a sample sheet."
                  embedded
                />
                {sampleError && (
                  <p
                    className="text-sm text-od-danger"
                    role="status"
                    data-testid="sample-sheet-error"
                  >
                    {sampleError}
                  </p>
                )}
              </div>
            )}

            {entryMode === "browse" && !link && !googleConnected && !failure && (
              <div className="space-y-3" data-testid="sheets-browse-signin">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-od-text">
                    Choose a sheet from Google Drive
                  </p>
                  <p className="text-sm text-od-muted">
                    Sign in with Google first.
                  </p>
                  <p className="text-sm text-od-muted">
                    Then pick the sheet you want to turn into a panel.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="primary"
                    size="sm"
                    loading={continuing}
                    onClick={() => void handleContinue()}
                    data-testid="sheets-continue"
                  >
                    Continue with Google
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="px-0"
                    onClick={() => {
                      setEntryMode("choice");
                      browseOpened.current = false;
                    }}
                  >
                    Back to choices
                  </Button>
                </div>
              </div>
            )}

            {entryMode === "browse" &&
              googleConnected &&
              !link &&
              !pickerGranted &&
              !probe &&
              !failure &&
              connectionId &&
              picker && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      void openPicker(connectionId, { mode: "browse" })
                    }
                    data-testid="sheets-open-browse-picker"
                  >
                    Choose from Google Drive
                  </Button>
                  <p className="text-sm text-od-muted" role="status">
                    Opening Google Drive…
                  </p>
                </div>
              )}

            {entryMode === "paste" && (editingLink || !link) && (
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  // New link mid-build: cancel the in-flight create before the
                  // next probe starts another createPanel.
                  createPanelAbort.current?.();
                  generationStarted.current = null;
                  datasourceIdRef.current = null;
                  setCreatedId(null);
                  setLink(linkInput.trim());
                  setEditingLink(false);
                  setProbe(null);
                  setFailure(null);
                  linkInputDirty.current = false;
                }}
              >
                {/*
                  MOSA-697 item 17. The field label is the only title in this
                  card: the screen title is in the rail. Item 16 took out the
                  "What happens next" box, which repeated the rail.
                */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="sheet-link"
                    className="block text-sm font-semibold text-od-text"
                  >
                    Paste your Google Sheet link
                  </label>
                  <Input
                    id="sheet-link"
                    size="md"
                    value={linkInput}
                    onChange={(event) => {
                      linkInputDirty.current = true;
                      setLinkInput(event.target.value);
                    }}
                    placeholder="Paste a Google Sheet link"
                    spellCheck={false}
                    autoComplete="off"
                    aria-invalid={
                      failure?.case === "invalid_link" ||
                      linkField.tone === "error"
                    }
                    aria-describedby={
                      failure?.case === "invalid_link"
                        ? "sheet-link-hint sheet-link-check sheet-link-error"
                        : "sheet-link-hint sheet-link-check"
                    }
                    data-testid="sheet-link-input"
                  />
                  <p id="sheet-link-hint" className="text-xs text-od-muted [overflow-wrap:anywhere]">
                    Example: {SHEETS_LINK_EXAMPLE}
                  </p>
                  {/*
                    MOSA-697 item 19. The answer while the user types. The node
                    stays in the markup when it is empty, so the live region is
                    already there when the first message arrives.
                  */}
                  <p
                    id="sheet-link-check"
                    role="status"
                    aria-live="polite"
                    className={`min-h-4 text-xs [overflow-wrap:anywhere] ${
                      linkField.tone === "error"
                        ? "text-od-danger"
                        : linkField.tone === "ok"
                          ? "text-od-success"
                          : "text-od-muted"
                    }`}
                    data-testid="sheet-link-check"
                  >
                    {linkField.message}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" variant="primary" size="sm">
                    Use this link
                  </Button>
                  {link && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingLink(false);
                        setLinkInput(link);
                        linkInputDirty.current = false;
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  {!link && (
                    <Button
                      variant="link"
                      size="sm"
                      className="px-0"
                      onClick={() => setEntryMode("choice")}
                    >
                      Back to choices
                    </Button>
                  )}
                </div>
              </form>
            )}

            {/*
              MOSA-697 item 18. The one place the full link is shown. The rail
              shows the sheet name or a short link instead. Item 21: Edit link
              is a button, because it was hard to see as plain muted text.
            */}
            {!editingLink && link && parsedOk && (
              <div className="rounded-lg border border-od-border bg-od-surface-2 px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-od-muted">
                      Sheet link
                    </p>
                    <p className="mt-1 break-all text-sm text-od-text">
                      {canonicalSheetsUrl(parsed.spreadsheetId, parsed.gid)}
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setEditingLink(true);
                      setLinkInput(link);
                    }}
                    data-testid="sheets-edit-link"
                  >
                    Edit link
                  </Button>
                </div>
              </div>
            )}

            {/*
              MOSA-407. One button for both cases. A visitor with no account
              gets the merged consent, which creates the account and grants the
              file in one Google screen; a signed-in user gets the ordinary
              connect. The separate sign-up page is gone from this path, and
              email sign-up stays available below.
            */}
            {linkReady && link && !googleConnected && !failure && !reading && (
              <div className="space-y-3">
                {/*
                  MOSA-697 item 20. What Google asks for, before the button.
                  The second line names the drive.file permission, because the
                  Google screen uses that wording for one-file access.
                */}
                <div className="space-y-1" data-testid="sheets-google-note">
                  <p className="text-sm text-od-text">
                    Google asks you to choose your account. Then it asks you to
                    allow access to this one file.
                  </p>
                  <p className="text-sm text-od-muted">
                    OnlyDash reads only that sheet, with the drive.file
                    permission. It cannot open your other Drive files.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="primary"
                    size="sm"
                    loading={continuing}
                    onClick={() => void handleContinue()}
                    data-testid="sheets-continue"
                  >
                    Continue with Google
                  </Button>
                  {!signedIn && (
                    <Button
                      variant="link"
                      size="sm"
                      className="px-0"
                      onClick={() => onAction("sign_up")}
                    >
                      Sign up with an email instead
                    </Button>
                  )}
                </div>
                {!signedIn && (
                  <p className="text-xs text-od-muted">
                    Continue with Google also creates your free OnlyDash
                    account.
                  </p>
                )}
              </div>
            )}

            {signedIn && googleEmail && googleConnected && (
              <ConnectedAs
                email={googleEmail}
                onSwitch={() => onAction("switch_account")}
              />
            )}

            {signedIn &&
              linkReady &&
              googleConnected &&
              picker &&
              !pickerGranted &&
              !probe &&
              !accessChecking &&
              connectionId && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void openPicker(connectionId)}
                    data-testid="sheets-open-picker"
                  >
                    {confirmAccessLabel}
                  </Button>
                </div>
              )}

            {accessChecking && !probe && (
              <p className="flex items-center gap-2 text-sm text-od-muted" role="status">
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-od-border border-t-od-primary"
                  aria-hidden
                />
                Checking access to your sheet…
              </p>
            )}

            {failure && (
              <div
                id={
                  failure.case === "invalid_link"
                    ? "sheet-link-error"
                    : undefined
                }
                className="rounded-lg border border-[color-mix(in_srgb,var(--od-danger)_35%,transparent)] bg-[color-mix(in_srgb,var(--od-danger)_8%,transparent)] p-4"
                role="alert"
                data-testid={`sheets-failure-${failure.case}`}
              >
                <h2 className="text-sm font-semibold text-od-text">
                  {failure.title}
                </h2>
                <p className="mt-1 text-sm text-od-text">{failure.explanation}</p>
                <p className="mt-1 text-sm text-od-text">{failure.fix}</p>
                {failure.case === "no_access" && googleEmail && (
                  <p className="mt-2 text-sm text-od-text">
                    {connectedAsLabel(googleEmail)}
                  </p>
                )}
                {panelFailed ? (
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-3"
                    onClick={() => void createPanel()}
                    loading={creating}
                  >
                    Retry
                  </Button>
                ) : (
                  failure.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {failure.actions.map((action, index) => (
                        <Button
                          key={action}
                          variant={index === 0 ? "primary" : "secondary"}
                          size="sm"
                          onClick={() => onAction(action)}
                          data-testid={`sheets-action-${action}`}
                          loading={action === "convert_to_sheet" && converting}
                          disabled={
                            action === "retry_later"
                              ? retryIn != null && retryIn > 0
                              : false
                          }
                        >
                          {action === "retry_later" &&
                          retryIn != null &&
                          retryIn > 0
                            ? `Retry in ${retryIn}s`
                            : actionLabel(action)}
                        </Button>
                      ))}
                    </div>
                  )
                )}
              </div>
            )}

            {probe && (
              <div className="space-y-4" data-testid="sheets-read">
                {creating && (
                  <div
                    className="rounded-xl border border-od-primary/25 bg-gradient-to-r from-[color-mix(in_srgb,var(--od-primary)_10%,transparent)] to-[color-mix(in_srgb,var(--od-accent)_8%,transparent)] p-4 shadow-sm"
                    data-testid="sheets-analyse-progress"
                  >
                    <p className="text-sm font-semibold text-od-text">
                      Analyzing your sheet
                    </p>
                    <p className="mt-1 text-sm text-od-text">{progressLabel}</p>
                    <p className="mt-0.5 text-xs text-od-muted">
                      {analyseElapsedLabel(analyseElapsed)} elapsed
                    </p>
                    <p className="sr-only" role="status" aria-live="polite">
                      {progressLabel}
                    </p>
                    <div
                      className="mt-3 h-2 overflow-hidden rounded-full bg-od-border/80"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuenow={progressTotal > 0 ? progressDone : undefined}
                      aria-valuemax={progressTotal > 0 ? progressTotal : undefined}
                      aria-label={progressLabel}
                    >
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-od-primary to-od-accent transition-[width] duration-300"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(
                              4,
                              progressTotal
                                ? Math.round((progressDone / progressTotal) * 100)
                                : 8,
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-od-muted">
                    Review
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold text-od-text">
                      {probe.title || "Your Google Sheet"}
                    </h2>
                    {!probe.canEdit && (
                      <span
                        className="rounded-full border border-od-border bg-od-surface-2 px-2 py-0.5 text-xs font-medium text-od-muted"
                        data-testid="sheets-read-only-badge"
                      >
                        Read-only
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-od-muted">
                    {reviewSummary(probe)}
                  </p>
                </div>

                {/* MOSA-408. Viewer or commenter: the panel still works, with
                    writes off. The API decides that from Drive, so this button
                    only says "build it anyway". */}
                {isViewerOnly(probe) && !readOnlyAccepted && (
                  <div
                    className="rounded-lg border border-od-border bg-od-surface-2 p-4"
                    data-testid="sheets-viewer-only"
                  >
                    <h3 className="text-sm font-semibold text-od-text">
                      You can view this sheet but not edit it
                    </h3>
                    <p className="mt-1 text-sm text-od-text">
                      {googleEmail
                        ? `${googleEmail} has view access only, so OnlyDash cannot write back to this sheet.`
                        : "This Google account has view access only, so OnlyDash cannot write back to this sheet."}
                    </p>
                    <p className="mt-1 text-sm text-od-text">
                      Connect it read-only to browse, filter and sort the rows.
                      To edit rows later, ask the owner for edit access, then
                      connect Google again.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onAction("connect_read_only")}
                        data-testid="sheets-action-connect_read_only"
                      >
                        {actionLabel("connect_read_only")}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onAction("request_edit_access")}
                        data-testid="sheets-action-request_edit_access"
                      >
                        {actionLabel("request_edit_access")}
                      </Button>
                    </div>
                  </div>
                )}

                {isViewerOnly(probe) && readOnlyAccepted && (
                  <p
                    className="text-sm text-od-muted"
                    data-testid="sheets-read-only-note"
                  >
                    This panel is read-only. Add and Edit stay off until the
                    owner gives this account edit access.
                  </p>
                )}

                {/* MOSA-408. No column names anywhere: either the sheet is
                    empty, or the names are not on the row we read. Both fixes
                    are here, and the per-tab chooser below handles the second. */}
                {needsHeaderRow(probe) && (
                  <div
                    className="rounded-lg border border-od-border bg-od-surface-2 p-4"
                    data-testid="sheets-no-header"
                  >
                    <h3 className="text-sm font-semibold text-od-text">
                      {isEmptySheet(probe)
                        ? "This sheet has no column names yet"
                        : "No tab has column names on the row we read"}
                    </h3>
                    <p className="mt-1 text-sm text-od-text">
                      OnlyDash builds each table from a row of column names.
                      Without that row there is nothing to put in a panel.
                    </p>
                    <p className="mt-1 text-sm text-od-text">
                      Type a name in each column of row 1, or pick the row your
                      names are already on. Then try again.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onAction("add_starter_headers")}
                        data-testid="sheets-action-add_starter_headers"
                      >
                        {actionLabel("add_starter_headers")}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onAction("use_template")}
                        data-testid="sheets-action-use_template"
                      >
                        {actionLabel("use_template")}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onAction("try_again")}
                        data-testid="sheets-action-try_again"
                      >
                        {actionLabel("try_again")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* The roll-up, so a long tab list does not hide the fact
                    that some tabs are left out. Each tab also says why. */}
                {skippedTabs(probe).length > 0 &&
                  selectedTabs(probe).length > 0 && (
                    <div
                      className="rounded-lg border border-od-border bg-od-surface-2 p-3"
                      data-testid="sheets-headerless-tabs"
                    >
                      <p className="text-sm text-od-text">
                        OnlyDash skips these tabs:{" "}
                        {skippedTabs(probe)
                          .map((tab) => `“${tab.title}”`)
                          .join(", ")}
                        . It uses the other tabs.
                      </p>
                    </div>
                  )}

                {pagedNote(probe) && (
                  <p
                    className="text-sm text-od-muted"
                    data-testid="sheets-paged-note"
                  >
                    {pagedNote(probe)}
                  </p>
                )}

                {truncatedTabsNote(probe) && (
                  <p
                    className="text-sm text-od-muted"
                    data-testid="sheets-tabs-truncated"
                  >
                    {truncatedTabsNote(probe)}
                  </p>
                )}

                <ul className="space-y-2">
                  {probe.tabs.map((tab) => {
                    const isCurrent =
                      creating &&
                      progressCurrentTab != null &&
                      progressCurrentTab === tab.title;
                    const used = selectedTabs(probe);
                    const isDoneTab =
                      creating &&
                      progressCurrentTab != null &&
                      used.findIndex((t) => t.title === tab.title) <
                        used.findIndex((t) => t.title === progressCurrentTab);
                    const tabComplete = createdId || (creating && isDoneTab);
                    const problems = tabProblems(tab);
                    return (
                    <li
                      key={tab.gid}
                      className="rounded-lg border border-od-border px-3 py-2.5"
                      data-testid={`sheets-tab-${tab.gid}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-semibold text-od-text">
                          {/* MOSA-668. While the review is still open and the
                              file has more than one usable tab, the tick is
                              the user's to change. */}
                          {pickingTabs ? (
                            <label className="-my-1.5 -ml-1 flex min-h-11 min-w-11 cursor-pointer items-center justify-center">
                              <input
                                type="checkbox"
                                checked={tab.selected}
                                disabled={!canSelectTab(tab)}
                                onChange={(event) =>
                                  onToggleTab(tab.gid, event.target.checked)
                                }
                                data-testid={`sheets-tab-select-${tab.gid}`}
                                aria-label={`Connect ${tab.title}`}
                              />
                            </label>
                          ) : isCurrent ? (
                            <span
                              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-od-primary border-t-transparent"
                              aria-hidden
                            />
                          ) : tabComplete || (!creating && tab.selected) ? (
                            <CheckIcon
                              sx={{ fontSize: 16 }}
                              className="text-od-success"
                              aria-hidden
                            />
                          ) : null}
                          {tab.title}
                        </span>
                        <span className="text-xs text-od-muted">
                          {tab.selected
                            ? `${tab.columns.length} column${
                                tab.columns.length === 1 ? "" : "s"
                              }${
                                tab.rowCount
                                  ? ` · ${tab.rowCount.toLocaleString()} rows`
                                  : ""
                              }`
                            : canSelectTab(tab)
                              ? "Not used"
                              : "No column names"}
                        </span>
                      </div>

                      {problems.map((problem) => (
                        <p
                          key={problem}
                          className="mt-1 text-xs text-od-text"
                          data-testid={`sheets-tab-problem-${tab.gid}`}
                        >
                          {problem}
                        </p>
                      ))}

                      {/* MOSA-408. A title banner pushes the real names down a
                          row or two. Re-read this one tab from there. Stay up
                          while the panel builds too: a one-usable-tab sheet
                          auto-starts (MOSA-668), and the blank tab still asks
                          the user to pick the row its names are on. */}
                      {!createdId && (
                        <label className="mt-2 flex flex-wrap items-center gap-2 text-xs text-od-muted">
                          <span>Column names are on row</span>
                          <select
                            className="min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 rounded-md border border-od-interactive-boundary bg-od-surface px-2 py-1 text-xs text-od-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface"
                            value={tab.headerRow}
                            disabled={headerRowBusy != null}
                            onChange={(event) =>
                              void reprobeHeaderRow(
                                tab.gid,
                                Number(event.target.value),
                              )
                            }
                            data-testid={`sheets-header-row-${tab.gid}`}
                            aria-label={`Header row for ${tab.title}`}
                          >
                            {HEADER_ROW_CHOICES.map((row) => (
                              <option key={row} value={row}>
                                {row}
                              </option>
                            ))}
                          </select>
                          {headerRowBusy === tab.gid && (
                            <span role="status">Reading row {tab.headerRow}…</span>
                          )}
                        </label>
                      )}

                      {tab.columns.length > 0 && (
                        <ul className="mt-2 flex flex-wrap gap-1.5">
                          {tab.columns.map((column) => {
                            const badges = columnBadges(column);
                            return (
                              <li
                                key={`${tab.gid}:${column.index}`}
                                className="flex items-center gap-1 rounded-md border border-od-border bg-od-surface-2 px-1.5 py-0.5 text-xs text-od-text"
                                data-testid={`sheets-column-${tab.gid}-${column.index}`}
                              >
                                <span>{column.name}</span>
                                {badges.map((badge) => (
                                  <span
                                    key={badge}
                                    className="rounded bg-od-border/70 px-1 text-[10px] font-medium text-od-muted"
                                    data-testid={`sheets-column-badge-${tab.gid}-${column.index}`}
                                  >
                                    {badge}
                                  </span>
                                ))}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                    );
                  })}
                </ul>

                {/* MOSA-668. A viewer-only file has its own connect button in
                    the card above, which carries the read-only answer too. */}
                {pickingTabs && !isViewerOnly(probe) && (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!canConnect(probe)}
                      onClick={() => setConnectPressed(true)}
                      data-testid="sheets-connect-tabs"
                    >
                      Connect
                    </Button>
                    {!canConnect(probe) && (
                      <p
                        className="text-sm text-od-muted"
                        data-testid="sheets-connect-tabs-hint"
                      >
                        Tick at least one tab to connect.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Exported for the tests: the step list is pure state derivation. */
export type { StepStatus, WizardStep };
